import React, { memo, useCallback, useMemo } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import {
  CombinedForm,
  Form,
  FormForge,
  ListViewer,
  SafeBoundary,
  Sod,
} from '../../../../shared/components';
import {
  CHAT_BLOCK,
  CHAT_MODE,
  RENDER_FORM_EXPANDED,
  RENDER_NORMAL,
  testIds,
} from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import {
  type ChatMessage,
  type ChatMode,
  type CombinedFormBlock,
  type CustomBlock,
  type FormBlock,
  type FormForgeBlock,
  type PanelBlocks,
  type SodBlock,
} from '../../../../shared/types';
import { chatModuleActions } from '../../data-store/chatModuleSlice';
import { getWorkflowType } from '../../utils';

import './ChatRightPanel.css';

interface ChatRightPanelProps {
  triggerAssistant?: (chatMessage: ChatMessage<ChatMode>) => void;
}

/**
 * ChatRightPanel
 */
function ChatRightPanel({ triggerAssistant }: ChatRightPanelProps) {
  const dispatch = useDispatch();

  const { renderMode, rightPanel } = useTypedSelector(
    (state) => state.savicopilot.chatModule
  );

  const { id: userId } = useTypedSelector((state) => state.user.userInfo);

  const renderBlock = useMemo(() => {
    if (rightPanel && rightPanel.blocks.length > 0) {
      return rightPanel.blocks[0] as PanelBlocks;
    }

    return null;
  }, [rightPanel]);

  const handleCollapse = useCallback(() => {
    batch(() => {
      dispatch(chatModuleActions.setRenderMode(RENDER_NORMAL));
      dispatch(chatModuleActions.setRightPanel(null));
    });
  }, [dispatch]);

  const getSubmitPayload = useCallback(
    (message: ChatMessage<ChatMode>) => ({
      ...message,
      mode: CHAT_MODE.USER,
      metadata: {
        ...message.metadata,
        timestamp: new Date().toISOString(),
      },
    }),
    []
  );

  const renderBody = useCallback(() => {
    if (!renderBlock) {
      return null;
    }

    if (renderBlock.type === CHAT_BLOCK.FORM) {
      const typedBlock = renderBlock as FormBlock;

      return (
        <SafeBoundary>
          <Form
            id=''
            isFullView
            title={typedBlock.title}
            data={typedBlock.elements}
            actions={typedBlock.actions}
            workflowType={getWorkflowType(
              rightPanel?.metadata.workflowInfo?.workflowName
            )}
            onCollapse={() => handleCollapse()}
            retentions={typedBlock.retentions}
            onSubmit={(formData) => {
              if (triggerAssistant && rightPanel) {
                triggerAssistant({
                  ...getSubmitPayload(rightPanel),
                  blocks: [
                    {
                      ...typedBlock,
                      elements: formData,
                    },
                  ],
                });
              }
            }}
          />
        </SafeBoundary>
      );
    }

    if (renderBlock.type === CHAT_BLOCK.FORMFORGE) {
      const typedBlock = renderBlock as FormForgeBlock;

      return (
        <SafeBoundary>
          <FormForge
            id=''
            isFullView
            currentSelection={typedBlock.selections}
            retentions={typedBlock.retentions}
            onCollapse={() => handleCollapse()}
            onSubmit={(formData) => {
              if (triggerAssistant && rightPanel) {
                triggerAssistant({
                  ...getSubmitPayload(rightPanel),
                  blocks: [
                    {
                      ...typedBlock,
                      formData,
                    },
                  ],
                });
              }
            }}
          />
        </SafeBoundary>
      );
    }

    if (renderBlock.type === CHAT_BLOCK.COMBINEDFORM) {
      const typedBlock = renderBlock as CombinedFormBlock;

      return (
        <SafeBoundary>
          <CombinedForm
            id=''
            isFullView
            title={typedBlock.title}
            userId={userId}
            retentions={typedBlock.retentions}
            customEndpoint={typedBlock.customEndpoint}
            formforgeEndpoint={typedBlock.formforgeEndpoint}
            onCollapse={() => handleCollapse()}
            onSubmit={(customFormData, externalFormData) => {
              if (triggerAssistant && rightPanel) {
                triggerAssistant({
                  ...getSubmitPayload(rightPanel),
                  blocks: [
                    {
                      ...typedBlock,
                      custom: {
                        ...typedBlock.custom,
                        formJson: customFormData,
                      },
                      external: {
                        ...typedBlock.external,
                        formData: externalFormData,
                      },
                    },
                  ],
                });
              }
            }}
          />
        </SafeBoundary>
      );
    }

    if (renderBlock.type === CHAT_BLOCK.CUSTOM) {
      const typedBlock = renderBlock as CustomBlock;

      return (
        <SafeBoundary>
          <ListViewer
            id=''
            isFullView
            variant={typedBlock.variant}
            workflowType={getWorkflowType(
              rightPanel?.metadata.workflowInfo?.workflowName
            )}
            endpointDetails={typedBlock.endpoint}
            retentions={typedBlock.retentions}
            onCollapse={() => handleCollapse()}
            onSubmit={(selections) => {
              if (triggerAssistant && rightPanel) {
                triggerAssistant({
                  ...getSubmitPayload(rightPanel),
                  blocks: [
                    {
                      ...typedBlock,
                      selections,
                    },
                  ],
                });
              }
            }}
          />
        </SafeBoundary>
      );
    }

    if (renderBlock.type === CHAT_BLOCK.SOD) {
      const typedBlock = renderBlock as SodBlock;

      return (
        <SafeBoundary>
          <Sod
            id=''
            isFullView
            onCollapse={() => handleCollapse()}
            data={typedBlock.object}
          />
        </SafeBoundary>
      );
    }

    return null;
  }, [
    getSubmitPayload,
    handleCollapse,
    renderBlock,
    rightPanel,
    triggerAssistant,
    userId,
  ]);

  return (
    <Box
      tag='div'
      className={cx(
        'ChatRightPanel',
        renderMode === RENDER_FORM_EXPANDED && 'ChatRightPanel--isFormExpanded'
      )}
      dataTestId={testIds.CHAT_RIGHT_PANEL}>
      {rightPanel && renderBody()}
    </Box>
  );
}

ChatRightPanel.defaultProps = {
  triggerAssistant: undefined,
};

export default memo(ChatRightPanel);
